<template>
    <tr class=" border-t border-indigo-300">
        <td class="px-2 py-4 text-sm font-medium whitespace-nowrap text-gray-400 dark:text-gray-500">
            {{ (edit ? computedAmount : pret.amount).toLocaleString('fr-FR') }} €
        </td>
        <td class="px-2 py-4 text-sm font-medium whitespace-nowrap text-gray-700 dark:text-gray-300">
            <span v-if="edit">
                <AppInput
                    v-model='form.mensualite'
                    id='mensualite'
                    :validations="['number', 'required']"
                    @hasError="handleFormError"
                    :check-on-create="false"
                    :check-on-unfocus="true"
                    icon="euro-sign"
                />
            </span>
            <span v-else>
                {{ pret.mensualite }} €
            </span>
        </td>
        <td class="px-2 py-4 text-sm font-medium text-gray-700 dark:text-gray-300">
            <span v-if="edit">
                <DatePicker
                    v-model='form.dateDebut'
                    id='dateDebut'
                    minimum-type="month"
                    maximum-type="month"
                />
            </span>
            <span v-else>
                {{ formatMonthYear(pret.date_debut) }}
            </span>
        </td>
        <td class="px-2 py-4 text-sm font-medium text-gray-700 dark:text-gray-300">
            <span v-if="edit">
                <DatePicker
                    v-model='form.dateFin'
                    id='dateFin'
                    minimum-type="month"
                    maximum-type="month"
                    align="right"
                />
            </span>
            <span v-else>
                {{ formatMonthYear(pret.date_fin) }}
            </span>
        </td>
        <td class="px-2 py-4 text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[120px]">
            <div class="w-full bg-gray-200 rounded-full h-2.5">
                <div class="bg-indigo-600 h-2.5 rounded-full" :style="{width: progress + '%'}"></div>
            </div>
            <span class="text-xs text-gray-500">{{ progress.toFixed(0) }} %</span>
        </td>
        <td class="px-2 py-4 text-sm whitespace-nowrap">
            <span
                v-if="!edit"
                class="flex items-center justify-end"
            >
                <button class="btn-secondary mr-1 px-3" @click="edit = true">
                    <font-awesome-icon class="text-sm" :icon="['fa', 'edit']" />
                </button>
                <button class="btn-danger" @click.prevent="confirmRemovePret(pret.uuid);">
                    <font-awesome-icon class="text-sm text-white" :icon="['fa', 'times']" />
                </button>
            </span>
            <span
                v-else
                class="flex items-center justify-end"
            >
                <button class="btn-secondary mr-1" @click="edit = false">
                    <font-awesome-icon class="text-md" :icon="['fa', 'undo']" />
                </button>
                <button class="btn-success" :class="checkFieldValid ? '' : 'cursor-not-allowed bg-emerald-300 border-emerald-300 hover:bg-emerald-300 hover:border-emerald-300'" @click="editPret()">
                    <font-awesome-icon v-if="editLoader" class="text-sm animate-spin" :icon="['fas', 'spinner']"  />
                    <font-awesome-icon v-else class="text-sm" :icon="['fa', 'check']" />
                </button>
            </span>
        </td>
    </tr>
</template>

<script>

import AppInput from './../../../form/AppInput';
import DatePicker from './../../../form/DatePicker';
import pretCalculator from './../../../../common/utils/PretCalculator';

export default {
    components: {
        AppInput,
        DatePicker
    },

    emits: ['refresh'],

    data() {
        return {
            edit: false,
            form: {},
            formInvalid: [],
            editLoader: false
        }
    },

    props: {
        pret: {
            type: Object,
            default() {
                return {}
            }
        },
    },

    created() {
        this.form = {...this.pret}
        this.form.dateDebut = this.pret.date_debut
        this.form.dateFin = this.pret.date_fin
    },

    computed: {
        checkFieldValid() {
            return this.formInvalid.length === 0 && this.form.mensualite && this.form.dateDebut && this.form.dateFin;
        },

        progress() {
            return pretCalculator.progress(this.pret);
        },

        computedAmount() {
            return (this.form.mensualite || 0) * pretCalculator.totalMonths({date_debut: this.form.dateDebut, date_fin: this.form.dateFin});
        }
    },

    methods: {
        handleFormError(event) {
            if (event.value && !this.formInvalid.includes(event.name)) {
                this.formInvalid.push(event.name);
            }

            if (!event.value && this.formInvalid.includes(event.name)) {
                this.formInvalid = this.formInvalid.filter(f => f !== event.name)
            }
        },

        confirmRemovePret(pretUuid) {
            this.$confirm({
                variant: 'danger',
                title: 'Supprimer le prêt',
                message: "Vous êtes sur le point de supprimer ce prêt. Cette action est irréversible, en êtes-vous sûr ?",
                confirmText: 'Supprimer',
                action: () => this.removePret(pretUuid),
            });
        },

        async removePret(pretUuid) {
            if (!this.$route.params.bienUuid || !pretUuid) {
                return;
            }

            await this.$api.bien.removePret(this.$route.params.bienUuid, pretUuid)
            .then(async () => {
                this.$toast({title: 'Succès', description: "Le prêt à bien été supprimé."}, this.$toastSuccess);
            })
            .catch((err) => {
                console.log(err);
                this.$toast({title: 'Erreur', description: "Une erreur est survenue, veuillez réessayer plus tard."}, this.$toastError);
            })

            this.$emit('refresh')
        },

        async editPret() {
            if (!this.$route.params.bienUuid || !this.form.uuid || !this.checkFieldValid) {
                return;
            }

            this.editLoader = true

            await this.$api.bien.updatePret(this.$route.params.bienUuid, this.form.uuid, {
                mensualite: this.form.mensualite,
                date_debut: this.form.dateDebut,
                date_fin: this.form.dateFin,
            })
            .then(async () => {
                this.$toast({title: 'Succès', description: "Le prêt à bien été enregistré."}, this.$toastSuccess);
                this.$emit('refresh')
            })
            .catch((err) => {
                console.log(err);
                this.$toast({title: 'Erreur', description: "Une erreur est survenue, veuillez réessayer plus tard."}, this.$toastError);
            })

            this.editLoader = false
            this.edit = false
        }
    }
};
</script>
